import { useEffect, useState, useRef } from "react";
import { AiOutlineArrowUp } from "react-icons/ai";
import { sendAiRequest, processDoc, getDocProcessingStatus } from "api/aiChat";
import { useAiChat } from "hooks/AiChatContext";
import { getExtractedPath } from "utilities/extractedPath";
import extractUUIDs from "components/layout/utils/extractUUIDs";
import TypingIndicator from "./TypingIndicator";
import ReactMarkdown from "react-markdown";
import { FaTrash } from 'react-icons/fa';
import { FiX } from "react-icons/fi";

// Module-level storage for intervals to persist across component unmounts
const activeIntervals = new Map<number, number>();
const activeCooldownIntervals = new Map<number, number>();

const MarkdownMessage = ({ text }: { text: string }) => {
  const formattedText = text.replace(/\\n/g, '\n');
  return (
    <div className="prose prose-sm text-white max-w-none">
      <ReactMarkdown>{formattedText}</ReactMarkdown>
    </div>
  );
};
const AiChat = ({ setIsMovementDisabled }: { setIsMovementDisabled: React.Dispatch<React.SetStateAction<number>> }) => {
  const baseUrl = getExtractedPath();
  const { currOrg, curSol } = extractUUIDs(baseUrl);
  const [request, setRequest] = useState<string>('');
  const [reply, setReply] = useState<string>('');
  const userId = crypto.randomUUID();
  const { 
    docToProcessId, 
    setCommand, 
    messageHistory, 
    setMessageHistory, 
    setAiChatShown,
    isProcessing,
    setIsProcessing,
    cooldownRemainingMs,
    setCooldownRemainingMs
  } = useAiChat();
  const messagesEndRef = useRef<HTMLDivElement | null>(null);

  // Prevent duplicate processing calls per file id (e.g. React StrictMode in dev)
  const processedDocsRef = useRef<Record<number, boolean>>({});
  const lastProcessingPercentRef = useRef<number | null>(null);
  const processingCooldownStartedRef = useRef<Record<number, boolean>>({});

  const formatDuration = (ms: number) => {
    const totalSeconds = Math.max(0, Math.floor(ms / 1000));
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    const minutesPart = minutes > 0 ? `${minutes} minute${minutes === 1 ? '' : 's'}` : '';
    const secondsPart = `${seconds} second${seconds === 1 ? '' : 's'}`;
    return minutesPart ? `${minutesPart} ${secondsPart}` : secondsPart;
  };

  const initiateDocProcessing = async (id: number) => {
    if (processedDocsRef.current[id]) {
      return;
    }
    processedDocsRef.current[id] = true;

    const response = await processDoc({ oid: currOrg, sid: curSol, file_id: id.toString() });

    if (response.status === 200 || response.status === 202) {
      console.log("Document processing started:", response.status, response.data);
      setIsProcessing(true);
      lastProcessingPercentRef.current = null;
      processingCooldownStartedRef.current[id] = false;

      setMessageHistory((prev) => [
        ...prev,
        [
          '',
          "We've started processing your document. You'll see live progress updates here as each page is analyzed.",
        ],
      ]);

      const formattedId = `${curSol}__${currOrg}__${id}`;
      const intervalId = window.setInterval(async () => {
        try {
          const statusResponse = await getDocProcessingStatus({ file_id: formattedId });
          console.log("Document processing status:", statusResponse.data);

          const { percent, filename, state, message } = statusResponse.data || {};

          // Push professional status updates into chat when percent changes
          if (typeof percent === 'number' && percent !== lastProcessingPercentRef.current) {
            lastProcessingPercentRef.current = percent;

            const baseLabel = filename ? `Document "${filename}"` : "Document";
            const statusText =
              percent < 100
                ? `${baseLabel} is currently being processed — ${percent}% completed.`
                : `${baseLabel} processing is now complete.`;

            setMessageHistory((prev) => [
              ...prev,
              [
                '',
                statusText
              ],
            ]);
          }

          // When processing is finished, stop polling and start cooldown timer
          if (state === 'completed' || percent === 100) {
            clearInterval(intervalId);
            activeIntervals.delete(id);

            if (!processingCooldownStartedRef.current[id]) {
              processingCooldownStartedRef.current[id] = true;

              setMessageHistory((prev) => [
                ...prev,
                [
                  '',
                  'Processing is complete. Please wait while we finalize your document before using the AI chat.',
                ],
              ]);

              const COOLDOWN_AFTER_COMPLETE_MS = 2 * 60 * 1000 + 15 * 1000;
              setCooldownRemainingMs(COOLDOWN_AFTER_COMPLETE_MS);

              // Clear any existing cooldown interval for this file
              if (activeCooldownIntervals.has(id)) {
                clearInterval(activeCooldownIntervals.get(id)!);
              }

              const interval = window.setInterval(() => {
                setCooldownRemainingMs((prev) => {
                  if (prev === null) return prev;
                  const next = prev - 1000;

                  if (next <= 0) {
                    window.clearInterval(interval);
                    activeCooldownIntervals.delete(id);
                    setIsProcessing(false);
                    setCooldownRemainingMs(null);
                    setMessageHistory((prevHistory) => [
                      ...prevHistory,
                      [
                        '',
                        'You can now use the AI chat to ask questions about your processed document.',
                      ],
                    ]);
                    return null;
                  }

                  return next;
                });
              }, 1000);

              activeCooldownIntervals.set(id, interval);
            }
          }

          // Stop polling on non-successful HTTP codes
          if (statusResponse.status >= 400) {
            console.warn("Stopping status polling due to error status:", statusResponse.status);
            clearInterval(intervalId);
            activeIntervals.delete(id);
            setIsProcessing(false);

            setMessageHistory((prev) => [
              ...prev,
              [
                '',
                'We encountered an issue while processing your document. Please try again or contact support if the problem persists.'
              ],
            ]);
          }
        } catch (error) {
          console.error("Error while fetching document processing status, stopping polling:", error);
          clearInterval(intervalId);
          activeIntervals.delete(id);
          setIsProcessing(false);

          setMessageHistory((prev) => [
            ...prev,
            [
              '',
              'We were unable to retrieve the latest processing status. Please try again in a moment.'
            ],
          ]);
        }
      }, 10000);

      // Store interval so it persists across component unmounts
      activeIntervals.set(id, intervalId);

      // Optional: stop polling after some time (e.g., 10 minutes) to avoid infinite polling
      setTimeout(() => {
        if (activeIntervals.has(id)) {
          clearInterval(activeIntervals.get(id)!);
          activeIntervals.delete(id);
        }
      }, 10 * 60 * 1000);
    }
  };

  useEffect(() => {
    if (docToProcessId) {
      initiateDocProcessing(docToProcessId);
    }
  }, [docToProcessId]);

  // Restore cooldown timer if component remounts during cooldown
  useEffect(() => {
    if (cooldownRemainingMs !== null && cooldownRemainingMs > 0 && !activeCooldownIntervals.has(docToProcessId || 0)) {
      // Find the file ID that's in cooldown (check processedDocsRef)
      const fileId = Object.keys(processedDocsRef.current).find(id => 
        Number(id) && processingCooldownStartedRef.current[Number(id)]
      );
      
      if (fileId) {
        const id = Number(fileId);
        const interval = window.setInterval(() => {
          setCooldownRemainingMs((prev) => {
            if (prev === null) return prev;
            const next = prev - 1000;

            if (next <= 0) {
              window.clearInterval(interval);
              activeCooldownIntervals.delete(id);
              setIsProcessing(false);
              setCooldownRemainingMs(null);
              setMessageHistory((prevHistory) => [
                ...prevHistory,
                [
                  '',
                  'You can now use the AI chat to ask questions about your processed document.',
                ],
              ]);
              return null;
            }

            return next;
          });
        }, 1000);
        activeCooldownIntervals.set(id, interval);
      }
    }
  }, [cooldownRemainingMs, docToProcessId]);

  // Cleanup: Clear intervals when component unmounts (but keep them running in background)
  // The intervals are stored in module-level Map, so they continue running
  useEffect(() => {
    return () => {
      // Don't clear intervals on unmount - let them continue in background
      // They will be cleaned up when processing completes or errors
    };
  }, []);

  const handleComand = (command: any) => {
    setCommand(command);
  };

  const handleRequestSubmit = (e: any) => {
    e.preventDefault();
    if (isProcessing) {
      return;
    }
    setMessageHistory((prev) => [...prev, [request, '__typing__']]);
    setRequest('');

    const sendDataToIa = async () => {
      try {
        const response = await sendAiRequest({
          user_message: request.toString(),
          user_id: userId,
          oid: currOrg,
          sid: curSol
        });

        if (response.status === 200) {
          setMessageHistory((prev) => {
            const updated = [...prev];
            const lastItem = updated[updated.length - 1];
            updated[updated.length - 1] = [lastItem[0], response.data.reply];
            return updated;
          });
          if (response.data.command) {
            handleComand(response.data.command);
          }
        } else if (response.status >= 400 && response.status < 500) {
          setMessageHistory((prev) => {
            const updated = [...prev];
            const lastItem = updated[updated.length - 1];
            updated[updated.length - 1] = [
              lastItem[0],
              'Something went wrong, try again'
            ];
            return updated;
          });
        } else if (response.status >= 500) {
          setMessageHistory((prev) => {
            const updated = [...prev];
            const lastItem = updated[updated.length - 1];
            updated[updated.length - 1] = [
              lastItem[0],
              'We ran into an issue. Wait or try again'
            ];
            return updated;
          });
        }
      } catch (error) {
        console.error("AI request failed:", error);
        setMessageHistory((prev) => {
          const updated = [...prev];
          const lastItem = updated[updated.length - 1];
          updated[updated.length - 1] = [
            lastItem[0],
            'Unexpected error occurred. Please try again later'
          ];
          return updated;
        });
      }
    };

    sendDataToIa();
  };


  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messageHistory]);

  const MessageHistory = () => {
    return (
      <>
        {messageHistory.map((item, index) => (
          <div className="w-full flex gap-2 flex-wrap" key={index}>
            <div className="w-full flex items-end justify-end">
              <p className="max-w-[70%] bg-[#303030] p-4 rounded-xl">{item[0]}</p>
            </div>
            <div className="w-full items-start">
              <p className="max-w-[80%] w-max p-4 bg-[#606060] rounded-xl">
                {item[1] === '__typing__' ? (
                  <TypingIndicator />
                ) : (
                  <MarkdownMessage text={item[1]} />
                )}
              </p>
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </>
    );
  };

  const setBlockUnity = (state: number) => {
    setIsMovementDisabled(state);
  };

  return (
    <div 
      className="w-full h-full"
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onMouseUp={(e) => e.stopPropagation()}
      onMouseMove={(e) => e.stopPropagation()}
      onWheel={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
      onKeyUp={(e) => e.stopPropagation()}
      onKeyPress={(e) => e.stopPropagation()}
    >
      <div className="w-full h-full flex flex-col max-h-full relative">
        <div className="flex justify-end items-center gap-2 p-3">
          <div>
            <button
              type="button"
              onClick={() => setMessageHistory([])}
              className="group flex items-center overflow-hidden transition-all duration-300 ease-in-out border-2 border-lightGray rounded-2xl px-2 py-1 w-[38px] hover:w-[150px]"
            >
              <span className="transition-opacity duration-200 group-hover:opacity-0 group-hover:absolute">
                <FaTrash />
              </span>
              <span className="whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                Clear chat history
              </span>
            </button>
          </div>
          <button
            className="cursor-pointer bg-blue rounded-md p-1 h-min"
            onClick={() => setAiChatShown(false)}
          >
            <FiX size={20} />
          </button>
        </div>
        <div className="flex-grow p-4 overflow-y-auto flex flex-col gap-2">
          <MessageHistory />
        </div>
        <div className="h-max bg-gray-300 px-4 pt-2 pb-4 min-h-24">
          <form
            className="relative h-full pb-6 bg-[#303030] outline outline-1 outline-[#424242]/70 rounded-3xl"
            onSubmit={handleRequestSubmit}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
            onKeyUp={(e) => e.stopPropagation()}
          >
            {cooldownRemainingMs !== null && (
              <div className="px-4 pt-2 text-xs text-slate-200">
                Please wait {formatDuration(cooldownRemainingMs)} before using the AI chat so we can finish preparing your document.
              </div>
            )}
            <textarea
              disabled={isProcessing}
              onInput={(e: any) => {
                e.target.style.height = 'auto';
                e.target.style.height = `${e.target.scrollHeight}px`;
                setRequest(e.target.value);
              }}
              onKeyDown={(e: React.KeyboardEvent<HTMLTextAreaElement>) => {
                e.stopPropagation();
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleRequestSubmit(e);
                }
              }}
              onKeyUp={(e) => e.stopPropagation()}
              onKeyPress={(e) => e.stopPropagation()}
              onFocus={(e) => {
                e.stopPropagation();
                setBlockUnity(1);
                // Explicitly blur Unity canvas to release keyboard focus
                const unityCanvas = document.getElementById("unity-canvas") as HTMLCanvasElement;
                if (unityCanvas) {
                  unityCanvas.blur();
                }
                // Force focus on textarea
                e.currentTarget.focus();
              }}
              onBlur={(e) => {
                e.stopPropagation();
                setBlockUnity(0);
              }}
              onMouseDown={(e) => {
                e.stopPropagation();
                // Force focus when clicking
                e.currentTarget.focus();
              }}
              tabIndex={0}
              onClick={(e) => e.stopPropagation()}
              rows={1}
              value={request}
              placeholder="Ask anything"
              className="w-full resize-none bg-transparent overflow-hidden focus:outline-none pt-2 pb-3 px-4 max-h-[200px] overflow-y-auto mb-1"
            />
            <div className="absolute bottom-1.5 w-full flex justify-end px-1.5">
              <button className="bg-white rounded-full p-1.5 disabled:opacity-50 disabled:cursor-not-allowed" type="submit" disabled={isProcessing}>
                <AiOutlineArrowUp size={20} className="text-black" />
              </button>
            </div>
          </form>
        </div>
      </div>
    </div >
  );
};

export default AiChat;